/**
 * Conquest system - bonus calculations and state checks
 * Headless - no GUI
 */

import type { GameState } from '../state.js';
import { COUNTRIES } from '../data/conquest.js';
import { PRODUCERS, getMilestoneMultiplier } from '../data/producers.js';

/** Get total CPS multiplier from all conquest facilities */
export function getConquestCpsMult(state: GameState): number {
  let pct = 0;
  for (const country of COUNTRIES) {
    const cs = state.conquest?.countries[country.id];
    if (!cs?.facilities) continue;
    for (let i = 0; i < country.facilities.length; i++) {
      const fac = country.facilities[i];
      const lv = cs.facilities[i] || 0;
      if (fac.effectType === 'cps_pct') pct += fac.effectPerLevel * lv;
    }
  }
  return 1 + pct / 100;
}

/** Get total tap power multiplier from conquest facilities */
export function getConquestTapMult(state: GameState): number {
  let pct = 0;
  for (const country of COUNTRIES) {
    const cs = state.conquest?.countries[country.id];
    if (!cs?.facilities) continue;
    for (let i = 0; i < country.facilities.length; i++) {
      const fac = country.facilities[i];
      const lv = cs.facilities[i] || 0;
      if (fac.effectType === 'tap_pct') pct += fac.effectPerLevel * lv;
    }
  }
  return 1 + pct / 100;
}

/** Get offline efficiency bonus (additive %) */
export function getConquestOfflineBonus(state: GameState): number {
  let pct = 0;
  for (const country of COUNTRIES) {
    const cs = state.conquest?.countries[country.id];
    if (!cs?.facilities) continue;
    for (let i = 0; i < country.facilities.length; i++) {
      const fac = country.facilities[i];
      const lv = cs.facilities[i] || 0;
      if (fac.effectType === 'offline_pct') pct += fac.effectPerLevel * lv;
    }
  }
  return pct;
}

/** Get GC earn bonus (additive %) */
export function getConquestGcBonus(state: GameState): number {
  let pct = 0;
  for (const country of COUNTRIES) {
    const cs = state.conquest?.countries[country.id];
    if (!cs?.facilities) continue;
    for (let i = 0; i < country.facilities.length; i++) {
      const fac = country.facilities[i];
      const lv = cs.facilities[i] || 0;
      if (fac.effectType === 'gc_pct') pct += fac.effectPerLevel * lv;
    }
  }
  return pct;
}

/** Get golden event frequency bonus (additive %) */
export function getConquestGoldenBonus(state: GameState): number {
  let pct = 0;
  for (const country of COUNTRIES) {
    const cs = state.conquest?.countries[country.id];
    if (!cs?.facilities) continue;
    for (let i = 0; i < country.facilities.length; i++) {
      const fac = country.facilities[i];
      const lv = cs.facilities[i] || 0;
      if (fac.effectType === 'golden_pct') pct += fac.effectPerLevel * lv;
    }
  }
  return pct;
}

/** Get producer cost discount (additive %) */
export function getConquestProducerDiscount(state: GameState): number {
  let pct = 0;
  for (const country of COUNTRIES) {
    const cs = state.conquest?.countries[country.id];
    if (!cs?.facilities) continue;
    for (let i = 0; i < country.facilities.length; i++) {
      const fac = country.facilities[i];
      const lv = cs.facilities[i] || 0;
      if (fac.effectType === 'producer_discount') pct += fac.effectPerLevel * lv;
    }
  }
  return pct;
}

/** Check if a country is unlocked (first country always unlocked, rest need prev conquered) */
export function isCountryUnlocked(countryId: string, state: GameState): boolean {
  const country = COUNTRIES.find(c => c.id === countryId);
  if (!country) return false;
  if (country.order === 1) return true;
  const prev = COUNTRIES.find(c => c.order === country.order - 1);
  if (!prev) return false;
  return state.conquest?.countries[prev.id]?.conquered ?? false;
}

/** Check if all 10 countries conquered */
export function isAllConquered(state: GameState): boolean {
  if (!state.conquest) return false;
  return COUNTRIES.every(c => state.conquest.countries[c.id]?.conquered === true);
}

/** Count conquered countries */
export function getConqueredCount(state: GameState): number {
  if (!state.conquest) return 0;
  return COUNTRIES.filter(c => state.conquest.countries[c.id]?.conquered).length;
}

/** Tier-based infiltration multiplier per producer type */
const TIER_INFILTRATION_MULT: Record<string, number> = {
  informant: 0.1,
  hacker: 0.3,
  disguiser: 1,
  mindController: 3,
  secretLab: 8,
  mediaCell: 20,
  shadowGovt: 50,
  deepBase: 120,
  orbitalStation: 300,
  dimensionGate: 800,
  planetController: 2000,
  galacticBeacon: 5000,
};

/** Calculate total infiltration power from all producers + upgrades */
export function getInfiltrationPower(state: GameState): number {
  let power = 0;
  let totalAgents = 0;

  for (const prod of PRODUCERS) {
    const owned = state.producers[prod.id]?.count ?? 0;
    if (owned === 0) continue;
    totalAgents += owned;

    const tierMult = TIER_INFILTRATION_MULT[prod.id] ?? 1;
    const milestone = getMilestoneMultiplier(owned);

    power += owned * tierMult * milestone;
  }

  // Prestige upgrade: infiltrationMastery — +50% per level
  const masteryLv = state.prestigeUpgrades?.infiltrationMastery ?? 0;
  if (masteryLv > 0) power *= 1 + masteryLv * 0.5;

  // Prestige upgrade: agentNetwork — +5% per 100 agents per level
  const networkLv = state.prestigeUpgrades?.agentNetwork ?? 0;
  if (networkLv > 0 && totalAgents >= 100) {
    const stacks = Math.floor(totalAgents / 100);
    power *= 1 + stacks * networkLv * 0.05;
  }

  // Special upgrade: spyMaster — infiltration x2
  if (state.specialUpgradesBought?.includes('spyMaster')) {
    power *= 2;
  }

  return power;
}

